import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { getAuthCredentials } from "../../utils/auth";
import TitleSetter from "../../component/TitleSetter";
import SalesInventoryExport from "./SalesInventoryExport";

const fmtPct = (prev, curr) => {
  if (prev === 0) return "—";
  const diff = ((curr - prev) / prev) * 100;
  const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
  const color = diff > 0 ? "green" : diff < 0 ? "red" : "gray";
  return (
    <span style={{ color, fontWeight: "bold" }}>
      {arrow} {Math.abs(diff).toFixed(1)}%
    </span>
  );
};

const fmtPeso = (val) =>
  `₱${(val || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

export default function SalesInventory({ dateRange }) {
  const { user, token, posId } = getAuthCredentials();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuSummary, setMenuSummary] = useState([]);
  const [inventorySummary, setInventorySummary] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPredicted, setCurrentPredicted] = useState([]);
  const [previousPredicted, setPreviousPredicted] = useState([]);
  const [predOverallCurr, setPredOverallCurr] = useState({});
  const [predOverallPrev, setPredOverallPrev] = useState({});
  const [predPerMenuCurr, setPredPerMenuCurr] = useState({});
  const [actualPerMenuCurr, setActualPerMenuCurr] = useState({});

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/sales/inventory-report`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              posId,
              startDate: dayjs(dateRange[0]).format("YYYY-MM-DDTHH:mm:ss"),
              endDate: dayjs(dateRange[1]).format("YYYY-MM-DDTHH:mm:ss"),
            },
          }
        );
        const sorted = data.menuSummary.sort((a, b) => b.cost - a.cost);
        setMenuSummary(sorted);
        setInventorySummary(data.inventorySummary);

        setCurrentPredicted(data.currentPredictedInventory || []);
        setPreviousPredicted(data.previousPredictedInventory || []);

        const aggPred = (arr = []) => {
          const out = {};
          for (const m of arr) {
            for (const ing of m.predictedInventory || []) {
              const key = ing.inventoryId || ing.inventory;
              if (!out[key]) out[key] = { id: key, name: ing.inventory, unit: ing.unit, qty: 0 };
              out[key].qty += ing.qty;
            }
          }
          return out;
        };
        const currAgg = aggPred(data.currentPredictedInventory || []);
        const prevAgg = aggPred(data.previousPredictedInventory || []);
        setPredOverallCurr(currAgg);
        setPredOverallPrev(prevAgg);

        const perMenu = {};
        for (const m of data.currentPredictedInventory || []) {
          const byName = {};
          for (const ing of m.predictedInventory || []) {
            byName[ing.inventory] = ing.qty;
          }
          perMenu[m.menuName] = byName;
        }
        setPredPerMenuCurr(perMenu);

        const perMenuActual = {};
        for (const m of data.currentActualInventoryPerMenu || []) {
          const byName = {};
          for (const ing of m.actualInventory || []) {
            const key = ing.inventory;
            if (!byName[key]) byName[key] = { qty: 0, cost: 0, unit: ing.unit || "" };
            byName[key].qty  += Number(ing.qty || 0);
            byName[key].cost += Number(ing.cost || 0);
            if (!byName[key].unit && ing.unit) byName[key].unit = ing.unit;
          }
          perMenuActual[m.menuName || "Unattributed"] = byName;
        }
        setActualPerMenuCurr(perMenuActual);
      } catch (err) {
        console.error(err);
        setError("Failed to load inventory data");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [dateRange, posId, token]);

  if (loading)
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Typography color="error" align="center" sx={{ mt: 3 }}>
        {error}
      </Typography>
    );

  // filter & resort
  const filteredMenus = menuSummary
    .filter((m) => m.menu.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => b.cost - a.cost);

  // current selection
  const selected =
    filteredMenus.find((m) => m.menu === openMenu) || filteredMenus[0] || {};
  const { breakdown = [] } = selected;

  const curStart = dayjs(dateRange[0]);
  const curEnd   = dayjs(dateRange[1]);

  const daysSpan = curEnd.startOf("day").diff(curStart.startOf("day"), "day") + 1;
  const prevStart = curStart.subtract(daysSpan, "day");
  const prevEnd   = curStart.subtract(1, "day");

  const fmtRange = (s, e) => {
    const same = s.format("YYYY-MM-DD") === e.format("YYYY-MM-DD");
    const fs = s.format("MMMM D");
    const fe = e.format("MMMM D");
    return same ? fs : `${fs}–${fe}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <TitleSetter title="Inventory Usage by Menu" />

      {/* Export Button */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <SalesInventoryExport dateRange={dateRange} />
      </Box>

      {/* 1) Search + Menu list */}
      <Typography variant="h6" gutterBottom>
        Menu Items
      </Typography>
      <TextField
        placeholder="Search menu..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 1 }}
      />
      <Paper variant="outlined" sx={{ maxHeight: 240, overflow: "auto" }}>
        <List disablePadding>
          {filteredMenus.length > 0 ? (
            filteredMenus.map((m) => (
              <ListItemButton
                key={m.menu}
                selected={openMenu === m.menu}
                onClick={() => setOpenMenu(m.menu)}
              >
                <ListItemText
                  primary={m.menu}
                  secondary={`Sold: ${m.sold} · Cost: ${fmtPeso(m.cost)}`}
                />
              </ListItemButton>
            ))
          ) : (
            <Typography
              variant="body2"
              align="center"
              sx={{ p: 2, color: "text.secondary" }}
            >
              No matching menu items.
            </Typography>
          )}
        </List>
      </Paper>

      {/* 2) Per-menu breakdown */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6">
          Inventory Used for “{selected.menu}”
        </Typography>
        <TableContainer component={Paper} sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ingredient</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Qty Used</TableCell>
                <TableCell align="right">Ideal Predicted Used</TableCell>
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {breakdown.length > 0 ? (() => {
                const predictedByName = predPerMenuCurr[selected.menu] || {};
                const actualByName    = actualPerMenuCurr[selected.menu] || {};

                const names = new Set([
                  ...Object.keys(predictedByName),
                  ...Object.keys(actualByName),
                  ...breakdown.map(b => b.inventory)
                ]);

                const predIdx = {};
                for (const b of breakdown) predIdx[b.inventory] = b;

                const rows = Array.from(names).map(name => {
                  const predQty = Number(predictedByName[name] || 0);
                  const act     = actualByName[name] || {};
                  const unit    = act.unit || predIdx[name]?.unit || "";
                  const cost    = (typeof act.cost === "number" ? act.cost : predIdx[name]?.cost) || 0;
                  const qty     = Number(act.qty || 0); // ACTUAL qty used
                  return { inventory: name, unit, qty, __pred: predQty, cost };
                });

                return rows.map((b, i) => (
                  <TableRow key={i}>
                    <TableCell>{b.inventory}</TableCell>
                    <TableCell>{b.unit}</TableCell>
                    <TableCell align="right">{b.qty}</TableCell>
                    <TableCell align="right">{b.__pred || 0}</TableCell>
                    <TableCell align="right">{fmtPeso(b.cost)}</TableCell>
                  </TableRow>
                ));
              })() : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No usage data.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 3) Overall Inventory Consumption */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" gutterBottom>
          Overall Inventory Consumption
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          Previous: ({fmtRange(prevStart, prevEnd)}) | Current: ({fmtRange(curStart, curEnd)})
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Inventory</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Previous Qty Used</TableCell>
                <TableCell align="right">Prev Ideal Predicted Used</TableCell>
                <TableCell align="right">Current Qty Used</TableCell>
                <TableCell align="right">Curr Ideal Predicted Used</TableCell>
                <TableCell align="right">Δ % (Qty)</TableCell>
                <TableCell align="right">Previous Cost</TableCell>
                <TableCell align="right">Current Cost</TableCell>
                <TableCell align="right">Δ % (Cost)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventorySummary.length > 0 ? (
                inventorySummary.map((inv, i) => {
                  const { previousQty: pq, currentQty: cq } = inv;
                  const { previousCost: pC, currentCost: cC } = inv;
                  const idxByName = (agg) => {
                    const o = {};
                    Object.values(agg).forEach(v => { if (v.name) o[v.name] = v; });
                    return o;
                  };
                  const predCurrByName = idxByName(predOverallCurr);
                  const predPrevByName = idxByName(predOverallPrev);
                  const key = inv.inventoryId || inv.name;
                  const prevPred = (predOverallPrev[key]?.qty) ?? (predPrevByName[inv.name]?.qty) ?? 0;
                  const currPred = (predOverallCurr[key]?.qty) ?? (predCurrByName[inv.name]?.qty) ?? 0;
                  return (
                    <TableRow key={i}>
                      <TableCell>{inv.name}</TableCell>
                      <TableCell>{inv.unit}</TableCell>
                      <TableCell align="right">{pq}</TableCell>
                      <TableCell align="right">{prevPred}</TableCell>
                      <TableCell align="right">{cq}</TableCell>
                      <TableCell align="right">{currPred}</TableCell>
                      <TableCell align="right">{fmtPct(pq, cq)}</TableCell>
                      <TableCell align="right">{fmtPeso(pC)}</TableCell>
                      <TableCell align="right">{fmtPeso(cC)}</TableCell>
                      <TableCell align="right">{fmtPct(pC, cC)}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No inventory data available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
